import {createSelector, lruMemoize} from 'reselect'
import debounce from 'lodash/debounce.js'
import groupBy from 'lodash/groupBy.js'
import take from 'lodash/take.js'
import sortBy from 'lodash/sortBy.js'
import head from 'lodash/head.js'
import sanitizeHtml from 'sanitize-html'

import {showGlobalError} from '../ordoro/GlobalErrorMessage.js'
import {setForm, updateForm, formsSelector, getState} from '../store.js'
import api from '../common/api.js'
import delay from '../common/delay.js'
import {taskWebsocketsSelector} from './company.js'

export const TASKS = 'tasks'
export const VISIBLE_ACTIVITY_COUNT = 5
const TASK_ORDER = [
  'force_import_products_from_cart',
  'import_orders_from_cart',
  'import_fba_inventory',
  'export_products_to_cart',
]

function setupTasksForm() {
  if (!formsSelector(getState())[TASKS]) {
    setForm(TASKS, structuredClone(tasksSelector.default))
  }
}

export function setTasks(tasks) {
  setupTasksForm()

  let taskList = [...taskListSelector(getState())]

  for (const task of tasks) {
    const index = taskList.findIndex(({id}) => id === task.id)

    if (index !== -1) {
      taskList[index] = task
    } else {
      taskList.push(task)
    }
  }

  updateForm(TASKS, {
    list: sortBy(taskList, ['created']).reverse().slice(0, 20),
  })
}

function setTasksAreRunning(areRunning) {
  setupTasksForm()

  updateForm(TASKS, {areRunning})
}

export function tasksSelector(state) {
  return formsSelector(state)[TASKS] || tasksSelector.default
}
tasksSelector.default = {
  areRunning: false,
  list: [],
}

export function taskListSelector(state) {
  return tasksSelector(state).list
}

export function areTasksRunningSelector(state) {
  return tasksSelector(state).areRunning
}

export const activityTasksSelector = createSelector(taskListSelector, (tasks) =>
  take(
    sortBy(
      tasks.filter((task) => !task._linkdata),
      'created',
    ).reverse(),
    VISIBLE_ACTIVITY_COUNT,
  ),
)

export const activeCartTaskSelector = createSelector(
  taskListSelector,
  (tasks) => head(tasks.filter((task) => task.table_key === 'cart_task')),
)

export const activeImportProductTasksSelector = createSelector(
  taskListSelector,
  (tasks) =>
    tasks.filter(
      (task) => task.type === 'import_products_from_cart' && !task.complete,
    ),
)

export const mostRecentlyUpdatedTaskSelector = createSelector(
  taskListSelector,
  (tasks) => head(sortBy(tasks, ['updated']).reverse()),
)

export const getTaskStatus = lruMemoize((task) => {
  if (task.status) {
    return sanitizeHtml(task.status, {allowedTags: 'a'})
  }
  return 'Waiting for activity to begin...'
})

/**
 * This gets task activities which includes activities of both misc_task and task tables
 */
export async function getTasks() {
  const {json} = await api.get('/activity/', {limit: VISIBLE_ACTIVITY_COUNT})

  setTasks(json.activity)
}

/**
 * This gets tasks from task table of the export type
 */
export async function getExports() {
  const {json} = await api.get('/new_task/', {
    limit: VISIBLE_ACTIVITY_COUNT,
    table_key: 'export_task',
  })

  setTasks(json.task)
}

export async function getAreTasksRunning() {
  const {json} = await api.get('/activity/counts/')

  setTasksAreRunning(json.running)
}

export async function getAllTasksBase() {
  await Promise.all([
    getTasks(),
    getExports(),
    ...(taskWebsocketsSelector(getState()) ? [getAreTasksRunning()] : []),
  ])
}

// when task table gets updates
export const getAllTasksDebounced = debounce(getAllTasksBase, 60000, {
  trailing: true,
  leading: true,
})

// when misc_task table gets updates
export const getTasksDebounced = debounce(
  () =>
    Promise.all([
      getTasks(),
      ...(taskWebsocketsSelector(getState()) ? [getAreTasksRunning()] : []),
    ]),
  60000,
  {trailing: true, leading: true},
)

export async function getAllTasks() {
  try {
    await getAllTasksBase()
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error loading tasks.',
        details: err.message,
      },
      err,
    )
  }
}

async function createTaskBase(type, cartID, params) {
  const {json} = await api.post('/task/', {
    type,
    cart: cartID,
    params,
  })

  return json
}

export async function createTask(type, cartID, params) {
  const task = await createTaskBase(type, cartID, params)

  setTasks([task])

  return task
}

export async function createNewTask(type, params) {
  const {json} = await api.post('/new_task/', {
    type,
    params: params || {},
  })

  setTasks([json])

  checkRunningTasks()

  return json
}

export async function createTasks(tasks) {
  const sortedTasks = sortTasks(tasks)

  setTasks(
    await Promise.all(
      sortedTasks.map((task) =>
        createTaskBase(task.type, task.cart, task.params),
      ),
    ),
  )

  checkRunningTasks()
}

function sortTasks(unsortedTasks) {
  if (unsortedTasks.length < 2) {
    return unsortedTasks
  }

  let sortedTasks = []

  const groupedTasks = groupBy(unsortedTasks, 'type')

  TASK_ORDER.forEach((type) => {
    const tasks = groupedTasks[type]
    if (tasks) {
      sortedTasks = sortedTasks.concat(tasks)
    }
    delete groupedTasks[type]
  })

  Object.keys(groupedTasks).forEach((type) => {
    const tasks = groupedTasks[type]
    if (tasks) {
      sortedTasks = sortedTasks.concat(tasks)
    }
  })

  return sortedTasks
}

export const isRunningExportSelector = createSelector(
  taskListSelector,
  (tasks) =>
    !!tasks.find(
      (task) => task.table_key === 'export_task' && task.complete === false,
    ),
)

let pollTimeout
async function pollRunningTasks() {
  await getAreTasksRunning()

  const areTasksRunning = areTasksRunningSelector(getState())
  const isRunningExport = isRunningExportSelector(getState())

  if (isRunningExport) {
    await getAllTasks()
  } else if (!areTasksRunning && !isRunningExport) {
    await getAllTasks()
    pollTimeout = null
    return
  }

  pollTimeout = setTimeout(pollRunningTasks, 30 * 1000)
}

let checkRunningTasksMarker
export async function checkRunningTasks() {
  // This function is called when we want to watch a running task
  // We are always polling for tasks
  // So this runs initially and then after each user direction task action

  if (window.THIS_IS_A_TEST_ENV) {
    // Do not do anything in tests
    // It gets called without awaiting it so tests would start it but never wait for it
    // and the response lands in other tests
    return
  }

  if (taskWebsocketsSelector(getState())) {
    return
  }

  // if we are already polling then don't try again
  if (pollTimeout) {
    return
  }

  // block others from getting to try block
  if (checkRunningTasksMarker) {
    return
  }

  try {
    // we only want this try block to run one at a time
    checkRunningTasksMarker = {}

    // Indicate to the user that we're checking the running tasks
    setTasksAreRunning(true)

    // Wait for API to mark any newly created tasks as running
    await delay(500)

    await pollRunningTasks()
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error updating running tasks.',
        details: err.message || err.error_message,
      },
      err,
    )
  }

  checkRunningTasksMarker = null
}
